import { useEffect, useRef, useState } from 'react'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface ClickableCellWithTooltipProps {
  value: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
  tooltipContent?: React.ReactNode
}

export const ClickableCellWithTooltip: React.FC<
  ClickableCellWithTooltipProps
> = ({ value, onClick, className = '', tooltipContent }) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const checkOverflow = () => {
      const element = buttonRef.current
      if (element) {
        const isOverflow = element.scrollWidth > element.clientWidth
        setIsOverflowing(isOverflow)
      }
    }

    checkOverflow()

    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [value])

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <button
          ref={buttonRef}
          type="button"
          className={`block max-w-full p-0 font-medium text-left text-blue-600 truncate bg-transparent border-none cursor-pointer ${className}`}
          onClick={(e) => {
            e.stopPropagation()
            onClick?.(e)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>)
            }
          }}
        >
          {value}
        </button>
      </TooltipTrigger>
      {isOverflowing && (
        <TooltipContent className="max-w-xs break-words">
          <span>{tooltipContent || value}</span>
        </TooltipContent>
      )}
    </Tooltip>
  )
}
